import { mockDoctors } from "../../data/mockDoctors";
import { Appointment } from "../../types/appointment.definition";
import { TimeSlot } from "../../types/availability.definition";
import {
  Doctor,
  DoctorSettingsInfo,
  DoctorStats,
  DoctorStatsRequest,
} from "../../types/doctor";
import { Page, PageRequest } from "../../types/pagination.definition";
import { Patient } from "../../types/patient";
import { Specialty } from "../../types/specialty.definition";
import { toPage } from "../../utils/pagination-utils";
import {
  cancelDoctorLicence,
  deleteDoctorTimeSlotAvailability,
  getAllSpecialties,
  getAllTimeSlotsByDate,
  getAllTimeSlotsByDateRange,
  getAppointments,
  getAvailableTimeSlots,
  getAvailableTimeSlotsByDate,
  getAvailableTimeSlotsByDateRange,
  getDoctorAppointmentStats,
  getDoctorAvailabilities,
  getDoctorAvailabilitiesInRange,
  getDoctorById,
  getDoctorLicenceBillingDetail,
  getDoctorPatientDetails,
  getDoctorPatients,
  getDoctorLicence,
  getDoctorLicencePlans,
  getLicencePlans,
  getPatientById,
  purchaseDoctorLicence,
  renewDoctorLicence,
  upgradeDoctorLicence,
  updateDoctor,
  updateDoctorSettings,
} from "../../webclients/doctor.client";

export const doctorsApi = {
  getAllDoctors: async (): Promise<Doctor[]> => {
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    return mockDoctors;
  },

  /**
   * One page of the doctor's appointments, newest first (the session only
   * carries the 10 latest). `total` is the count of all of them (MED-1738).
   */
  getAppointments: async (
    doctorId: number,
    pageRequest: PageRequest = {}
  ): Promise<Page<Appointment>> => {
    const response = await getAppointments(String(doctorId), pageRequest);
    return toPage(response);
  },

  getDoctorById: async (id: number): Promise<Doctor> => {
    const response = await getDoctorById(id);
    return response.data;
  },

  // getDoctorsBySpecialty: async (specialty: string): Promise<Doctor[]> => {
  //   // Simulate API call
  //   await new Promise((resolve) => setTimeout(resolve, 800));
  //   return mockDoctors.filter(
  //     (d) => d.specialty.toLowerCase() === specialty.toLowerCase()
  //   );
  // },

  updateDoctorProfile: async (
    id: number,
    data: Partial<Doctor>
  ): Promise<Doctor> => {
    const response = await updateDoctor(id, data);
    return response.data;
  },

  getAvailableTimeSlots: async (id: number): Promise<TimeSlot[]> => {
    const response = await getAvailableTimeSlots(id);
    return response.data;
  },

  getDoctorAvailabilities: async (id: number): Promise<TimeSlot[]> => {
    const response = await getDoctorAvailabilities(id);
    return response.data;
  },

  getDoctorAvailabilitiesInRange: async (
    id: number,
    startDate?: string,
    endDate?: string
  ): Promise<TimeSlot[]> => {
    const response = await getDoctorAvailabilitiesInRange(
      id,
      startDate,
      endDate
    );
    return response.data;
  },

  updateDoctorSettings: async (
    id: number,
    data: DoctorSettingsInfo
  ): Promise<DoctorSettingsInfo> => {
    const response = await updateDoctorSettings(id, data);
    return response.data;
  },

  getAvailableTimeSlotsByDate: async (
    doctorId: number,
    date: string
  ): Promise<TimeSlot[]> => {
    const response = await getAvailableTimeSlotsByDate(doctorId, date);
    return response.data;
  },

  getAvailableTimeSlotsByDateRange: async (
    doctorId: number,
    startDate: string,
    endDate: string
  ): Promise<TimeSlot[]> => {
    const response = await getAvailableTimeSlotsByDateRange(
      doctorId,
      startDate,
      endDate
    );
    return response.data;
  },

  getAllTimeSlotsByDate: async (
    doctorId: number,
    date: string
  ): Promise<TimeSlot[]> => {
    const response = await getAllTimeSlotsByDate(doctorId, date);
    return response.data;
  },

  getAllTimeSlotsByDateRange: async (
    doctorId: number,
    startDate: string,
    endDate?: string | null
  ): Promise<TimeSlot[]> => {
    const response = await getAllTimeSlotsByDateRange(
      doctorId,
      startDate,
      endDate
    );
    return response.data;
  },

  getPatientById: async (id: number): Promise<Patient> => {
    const response = await getPatientById(id);
    return response.data;
  },

  getDoctorPatients: async (doctorId: number): Promise<Patient[]> => {
    const response = await getDoctorPatients(doctorId);
    return response.data;
  },

  getDoctorPatientDetails: async (
    doctorId: number,
    patientId: number
  ): Promise<Patient> => {
    const response = await getDoctorPatientDetails(doctorId, patientId);
    return response.data;
  },

  getDoctorAppointmentStats: async (
    doctorId: number,
    data: DoctorStatsRequest
  ): Promise<DoctorStats> => {
    const response = await getDoctorAppointmentStats(doctorId, data);
    return response.data;
  },

  deleteTimeSlot: async (
    doctorId: number,
    availabilityId: number,
    timeSlotId: number
  ): Promise<void> => {
    await deleteDoctorTimeSlotAvailability(
      doctorId,
      availabilityId,
      timeSlotId
    );
  },

  getAllSpecialties: async (language?: string): Promise<Specialty[]> => {
    const response = await getAllSpecialties(language);
    return response.data;
  },

  getLicencePlans: async () => {
    const response = await getLicencePlans();
    return response.data as import("../../types/licence").LicencePlan[];
  },

  purchaseLicence: async (
    planType: string,
    contractDuration: string,
    whatsappDiscount?: boolean
  ): Promise<import("../../types/licence").DoctorLicence> => {
    const response = await purchaseDoctorLicence(
      planType,
      contractDuration,
      whatsappDiscount
    );
    return response.data as import("../../types/licence").DoctorLicence;
  },

  getLicenceBillingDetail: async (
    doctorId: number,
    licenceId: number
  ): Promise<import("../../types/licence").DoctorLicenceBillingDetail> => {
    return getDoctorLicenceBillingDetail(doctorId, licenceId);
  },

  cancelLicence: async (doctorId: number, licenceId: number): Promise<void> => {
    return cancelDoctorLicence(doctorId, licenceId);
  },

  getLicence: async (): Promise<
    import("../../types/licence").DoctorLicence
  > => {
    return getDoctorLicence();
  },

  getActiveLicencePlans: async (
    lang: string
  ): Promise<import("../../types/licence").DoctorLicencePlan[]> => {
    return getDoctorLicencePlans(lang);
  },

  upgradeLicence: async (
    doctorId: number,
    licenceId: number,
    planType: string,
    contractDuration: string
  ): Promise<import("../../types/licence").DoctorLicence> => {
    return upgradeDoctorLicence(
      doctorId,
      licenceId,
      planType,
      contractDuration
    );
  },

  renewLicence: async (
    planType: string
  ): Promise<import("../../types/licence").DoctorLicence> => {
    return renewDoctorLicence(planType);
  },
};
